import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode } from "react";
import { matchPath, Navigate } from "react-router-dom";

const supplierDetailsBreadcrumb = breadcrumbSlot();
const purchaseOrderBreadcrumb = breadcrumbSlot();
const purchaseOrderViewBreadcrumb = breadcrumbSlot();
const shipmentViewBreadcrumb = breadcrumbSlot();
const shipmentDetailBreadcrumb = breadcrumbSlot();
const productFamilyDetailBreadcrumb = breadcrumbSlot();
const productDetailBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = [
  "/catalog/products",
  "/catalog/products/:productFamilyId",
  "/catalog/products/:productFamilyId/:productId",
  "/catalog/consignment",
  "/catalog/consigned-product-families/:productFamilyId",
  "/pricing-rules",
  "/adjustments",
  "/products",
  "/liquidation/products",
  "/suppliers",
  "/suppliers/:supplierId",
  "/purchase-orders",
  "/purchase-orders/:purchaseOrderId",
  "/shipments",
  "/shipments/:viewName",
  "/shipments/:viewName/:shipmentId",
  "/forecasting",
  "/managed-returns",
  "/consignment",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

export const inventoryRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Inventory" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="products" /> },
      {
        path: "products",
        handle: "Products",
        async lazy() {
          const { InventoryProducts } = await import(
            "@redotech/redo-merchant-inventory-app/inventory-products/inventory-products"
          );
          return { Component: InventoryProducts };
        },
      },
      {
        path: "catalog",
        handle: { breadcrumb: "Catalog" },
        children: [
          {
            index: true,
            element: <Navigate replace to="products" />,
          },
          {
            path: "products",
            handle: { breadcrumb: "Products" },
            async lazy() {
              const { CatalogProductsWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/catalog/catalog-products-wrapper"
              );
              return {
                element: (
                  <CatalogProductsWrapper
                    productBreadcrumb={productDetailBreadcrumb}
                    productFamilyBreadcrumb={productFamilyDetailBreadcrumb}
                  />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { CatalogProducts } = await import(
                    "@redotech/redo-merchant-inventory-app/catalog-products"
                  );
                  return { Component: CatalogProducts };
                },
              },
              {
                path: ":productFamilyId",
                handle: { breadcrumb: productFamilyDetailBreadcrumb },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { ProductFamilyDetail } = await import(
                        "@redotech/redo-merchant-inventory-app/product-families/product-family-detail"
                      );
                      return { Component: ProductFamilyDetail };
                    },
                  },
                  {
                    path: ":productId",
                    handle: { breadcrumb: productDetailBreadcrumb },
                    async lazy() {
                      const { RedoProductDetail } = await import(
                        "@redotech/redo-merchant-inventory-app/products/redo-product-detail"
                      );
                      return { Component: RedoProductDetail };
                    },
                  },
                ],
              },
            ],
          },
          {
            path: "consigned-product-families/:productFamilyId",
            handle: { breadcrumb: productFamilyDetailBreadcrumb },
            async lazy() {
              const { ProductFamilyDetail } = await import(
                "@redotech/redo-merchant-inventory-app/product-families/product-family-detail"
              );
              return {
                element: (
                  <RedoOutletAccessGuard>
                    <ProductFamilyDetail />
                  </RedoOutletAccessGuard>
                ),
              };
            },
          },

          {
            path: "consignment",
            handle: { breadcrumb: "Consignment" },
            async lazy() {
              const { CatalogConsignmentProducts } = await import(
                "@redotech/redo-merchant-inventory-app/catalog-consignment/catalog-consignment-products"
              );
              return {
                Component: () => (
                  <RedoOutletAccessGuard>
                    <CatalogConsignmentProducts />
                  </RedoOutletAccessGuard>
                ),
              };
            },
          },
        ],
      },
      {
        path: "pricing-rules",
        handle: { breadcrumb: "Pricing Rules" },
        async lazy() {
          const { PricingRules } = await import(
            "@redotech/redo-merchant-inventory-app/pricing-rules/pricing-rules"
          );
          return { Component: PricingRules };
        },
      },
      {
        path: "adjustments",
        handle: { breadcrumb: "Inventory Adjustments" },
        async lazy() {
          const { InventoryAdjustments } = await import(
            "@redotech/redo-merchant-inventory-app/adjustments/inventory-adjustments"
          );
          return { Component: InventoryAdjustments };
        },
      },
      {
        path: "reports",
        handle: { breadcrumb: "Reports" },
        async lazy() {
          const { InventoryReporting } = await import(
            "@redotech/redo-merchant-inventory-app/reporting/inventory-reporting"
          );
          return { Component: InventoryReporting };
        },
      },
      {
        path: "forecasting",
        handle: { breadcrumb: "Forecasting" },
        children: [
          {
            index: true,
            async lazy() {
              const { Forecasting } = await import(
                "@redotech/redo-merchant-inventory-app/forecasting/forecasting"
              );
              return { Component: Forecasting };
            },
          },
        ],
      },
      {
        path: "suppliers",
        handle: { breadcrumb: "Suppliers" },
        children: [
          {
            index: true,
            async lazy() {
              const { SuppliersPage } = await import(
                "@redotech/redo-merchant-inventory-app/suppliers/suppliers"
              );
              return { Component: SuppliersPage };
            },
          },
          {
            path: ":supplierId",
            handle: { breadcrumb: supplierDetailsBreadcrumb },
            async lazy() {
              const { SupplierDetailsPage } = await import(
                "@redotech/redo-merchant-inventory-app/suppliers/supplier-detail"
              );
              return {
                element: (
                  <SupplierDetailsPage breadcrumb={supplierDetailsBreadcrumb} />
                ),
              };
            },
          },
        ],
      },
      {
        path: "purchase-orders",
        handle: { breadcrumb: "Purchase Orders" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: ":viewName",
            handle: { breadcrumb: purchaseOrderViewBreadcrumb },
            async lazy() {
              const { PurchaseOrdersWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-orders"
              );
              return {
                element: (
                  <PurchaseOrdersWrapper
                    breadcrumb={purchaseOrderViewBreadcrumb}
                  />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PurchaseOrdersPage } = await import(
                    "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-orders"
                  );
                  return { Component: PurchaseOrdersPage };
                },
              },
              {
                path: ":purchaseOrderId",
                handle: { breadcrumb: purchaseOrderBreadcrumb },
                async lazy() {
                  const { PurchaseOrderPage } = await import(
                    "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-order-detail"
                  );
                  return {
                    element: (
                      <PurchaseOrderPage breadcrumb={purchaseOrderBreadcrumb} />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "shipments",
        handle: { breadcrumb: "Shipments" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: ":viewName",
            handle: { breadcrumb: shipmentViewBreadcrumb },
            async lazy() {
              const { ShipmentsWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/shipments/shipments"
              );
              return {
                element: (
                  <ShipmentsWrapper breadcrumb={shipmentViewBreadcrumb} />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ShipmentsPage } = await import(
                    "@redotech/redo-merchant-inventory-app/shipments/shipments"
                  );
                  return { Component: ShipmentsPage };
                },
              },
              {
                path: ":shipmentId",
                handle: { breadcrumb: shipmentDetailBreadcrumb },
                async lazy() {
                  const { ShipmentDetailPage } = await import(
                    "@redotech/redo-merchant-inventory-app/shipments/shipment-detail"
                  );
                  return {
                    element: (
                      <ShipmentDetailPage
                        breadcrumb={shipmentDetailBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "managed-returns",
        handle: { breadcrumb: "Managed Returns" },
        async lazy() {
          const { ManagedReturnsDashboard } = await import(
            "@redotech/redo-merchant-inventory-app/managed-returns/managed-returns-dashboard"
          );
          return {
            Component: () => (
              <ManagedReturnsAccessGuard>
                <ManagedReturnsDashboard />
              </ManagedReturnsAccessGuard>
            ),
          };
        },
      },
      {
        path: "liquidation/dashboard",
        handle: { breadcrumb: "Home" },
        async lazy() {
          const { LiquidationPage } = await import(
            "@redotech/redo-merchant-inventory-app/liquidation/liquidation-page"
          );
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <LiquidationPage />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
      {
        path: "liquidation/products",
        handle: { breadcrumb: "All Listings" },
        async lazy() {
          const { MarketplaceListings } = await import(
            "@redotech/redo-merchant-inventory-app/marketplace/marketplace-listings"
          );
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <MarketplaceListings />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
    ],
  },
];

export const MarketplaceAccessGuard = memo(function MarketplaceAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;

  if (!isMarketplaceEnabled) {
    // Redirect to main inventory page if marketplace is not enabled
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});

export const ManagedReturnsAccessGuard = memo(
  function ManagedReturnsAccessGuard({ children }: { children: ReactNode }) {
    const team = useRequiredContext(TeamContext);
    const routes = useRequiredContext(MerchantRoutesContext);

    const isManagedReturnsEnabled = team.settings.redoManagedReturns?.enabled;

    if (!isManagedReturnsEnabled) {
      // Redirect to main inventory page if managed returns is not enabled
      return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
    }

    return <>{children}</>;
  },
);

export const RedoOutletAccessGuard = memo(function RedoOutletAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  // only available to the Redo Outlet team
  if (team._id !== "68800d96229f8fc10f4dc1ce") {
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});
